/* IBM Confidential
 *
 * OCO Source Materials
 *
 * IBM Cognos Products: ba-ui-toolkit
 * (C) Copyright IBM Corp. 2019
 *
 * The source code for this program is not published or otherwise
 * divested of its trade secrets, irrespective of what has been
 * deposited with the U.S. Copyright Office.
 * NLS_ENCODING=UTF-8
 * NLS_MESSAGEFORMAT_NONE
 */

import React, { Component } from 'react';
import ReactDOM from 'react-dom';
import PropTypes from 'prop-types';
import { classnames as cx, propsUtils, domUtils } from '@ba-ui-toolkit/utils';
import SVGIcon from '@ba-ui-toolkit/svgicon';
import closeIcon from '@ba-ui-toolkit/ba-graphics/dist/icons-js/close_16';
import Paragraph from '@ba-ui-toolkit/paragraph';
import Link from '@ba-ui-toolkit/link';
import Button from '@ba-ui-toolkit/button';
import ToolTip from '@ba-ui-toolkit/tooltip';
import styles from './Flyout.scss';
import getSpecs from './FlyoutHelperFunctions.js';
import stringResource from './nls/StringResource';

const { enableFocusLoop, disableFocusLoop } = domUtils;

const flyoutClassName = styles.flyoutClassName.replace(/"/g, '');
const { sanitize } = propsUtils;
const POSITIONS = [
	'top',
	'topLeft',
	'topCentred',
	'topRight',
	'bottom',
	'bottomLeft',
	'bottomCentred',
	'bottomRight',
	'left',
	'leftTop',
	'leftCentred',
	'leftBottom',
	'right',
	'rightTop',
	'rightCentred',
	'rightBottom',
];

function triggerNodePropValidator(props, propName, componentName) {
	if (props.triggerNode !== undefined && props.domNodeToAttachTo === undefined) {
		return new Error(
			`Invalid prop '${propName}' supplied to '${componentName}'. If a 'triggerNode' argument is passed, a 'domNodeToAttachTo' argument must also be passed.`
		);
	}

	return null;
}

export default class Flyout extends Component {
	static propTypes = {
		placement: PropTypes.oneOf(POSITIONS),
		candidatePlacement: PropTypes.array,
		hideArrow: PropTypes.bool,
		padding: PropTypes.string,
		children: PropTypes.node,
		/** customPropTypes: Element */
		triggerNode: PropTypes.instanceOf(Element) && triggerNodePropValidator,
		domNodeToAttachTo: PropTypes.instanceOf(Element),
		className: PropTypes.string,
		offset: PropTypes.number,
		onClose: PropTypes.func,
		showX: PropTypes.bool,
		// also allows to pass a react element
		title: PropTypes.oneOfType([PropTypes.string, PropTypes.element]),
		textContent: PropTypes.oneOfType([PropTypes.string, PropTypes.element]),
		link: PropTypes.element,
		href: PropTypes.string,
		linkName: PropTypes.string,
		style: PropTypes.object,
		startingFocusIndex: PropTypes.number,
		fixedPosition: PropTypes.shape({
			x: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
			y: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
		}),
	};

	static defaultProps = {
		placement: 'right',
		className: '',
		style: {},
		candidatePlacement: POSITIONS,
		startingFocusIndex: -1,
	};

	constructor(props) {
		super(props);

		this.state = {
			placement: props.placement,
			hidden: false,
			isMounted: false,
		};
		this._triggerNode = props.triggerNode;
	}

	componentDidMount() {
		if (!this._triggerNode) this._triggerNode = this._flyout.parentNode;
		this.setState({ firstMount: true });

		if (this._isFixedPosititonDefined()) {
			this._updateFixedPosition();
		} else {
			this._updateLocation();
		}

		window.addEventListener('resize', this._updateLocation);
		if (this.props.onClose) {
			document.addEventListener('mousedown', this._onClose);
			document.addEventListener('touchstart', this._onClose);
		}
	}

	componentWillUnmount() {
		window.removeEventListener('resize', this._updateLocation);
		if (this.props.onClose) {
			document.removeEventListener('mousedown', this._onClose);
			document.removeEventListener('touchstart', this._onClose);
		}
		disableFocusLoop(this._flyout);
	}

	componentDidUpdate() {
		const { startingFocusIndex } = this.props;
		// enable focus loop only after element has been mounted, and location has been updated
		if (this.state.firstMount) {
			enableFocusLoop(this._flyout, startingFocusIndex);
			this.setState({ firstMount: false });
		}
		if (!this._isFixedPosititonDefined()) {
			this._updateLocation();
		}
	}

	_updateFixedPosition() {
		const { fixedPosition: position } = this.props;
		const containerRect = this._flyout.getBoundingClientRect();
		const width = containerRect.width + 3;
		const height = containerRect.height + 3;

		let left;
		if (window.innerWidth - position.x < width) {
			left = window.innerWidth - width;
		} else {
			left = position.x;
		}

		let top;
		if (window.innerHeight - position.y < height) {
			top = window.innerHeight - height;
		} else {
			top = position.y;
		}

		this.setState({ top, left });
	}

	_onClose = event => {
		if (
			this._flyout &&
			!this._flyout.contains(event.target) &&
			this._triggerNode.contains &&
			(this.props.fixedPosition || !this._triggerNode.contains(event.target))
		) {
			this.props.onClose();
		}
	};

	_onKeyDown = e => {
		if (e.keyCode === 27) {
			e.stopPropagation();
			this.props.onClose();
		}
	};

	_updateLocation = () => {
		if (this._flyout) {
			const options = {
				arrow: this._arrow,
				offset: this.props.offset,
				domNodeToAttachTo: this.props.domNodeToAttachTo,
			};
			const specs = getSpecs(
				this._triggerNode,
				this._flyout,
				this.props.placement,
				options,
				this.props.candidatePlacement
			);
			if (this.state.top !== specs.top || this.state.left !== specs.left) {
				this.setState({
					top: specs.top,
					left: specs.left,
					placement: specs.placement,
					hidden: specs.invalid,
					isMounted: true,
				});
			}
		}
	};

	_showX() {
		return (
			<span className={`${flyoutClassName}__container__showX`}>
				<ToolTip
					className={`${flyoutClassName}__closeX`}
					orient="top"
					title={stringResource.get('close')}
				>
					<Button
						variant="inline"
						data-tid="ba-common-flyout__close-button"
						onClick={() => {
							this.props.onClose();
						}}
						onTouchEnd={() => {
							this.props.onClose();
						}}
						aria-label={stringResource.get('close')}
					>
						<SVGIcon
							data-tid="ba-common-flyout__close-icon"
							iconId={closeIcon.id}
							size="small"
						/>
					</Button>
				</ToolTip>
			</span>
		);
	}

	_title(title) {
		if (typeof title === 'string') {
			return (
				<div
					data-tid="ba-common-flyout__title"
					className={`${flyoutClassName}__container__title`}
				>
					<Paragraph fontWeight="bold"> {title} </Paragraph>
				</div>
			);
		}

		return title;
	}

	_textContent(text) {
		const { href, link } = this.props;
		const noLinkClass = href || link ? '' : ' without-link';
		if (typeof text === 'string') {
			return (
				<div
					data-tid="ba-common-flyout__text"
					className={`${flyoutClassName}__container__textContent${noLinkClass}`}
				>
					<Paragraph> {text} </Paragraph>
				</div>
			);
		}

		return text;
	}

	_link(href, linkName, link) {
		if (!link && href && linkName) {
			return (
				<div
					data-tid="ba-common-flyout__link"
					className={`${flyoutClassName}__container__link`}
				>
					<Link target="_blank" href={href} tabIndex="0">
						{linkName}
					</Link>
				</div>
			);
		}

		return link;
	}

	_isFixedPosititonDefined = () => this.props.fixedPosition != null;

	render() {
		const {
			padding,
			hideArrow,
			children,
			domNodeToAttachTo,
			className,
			showX,
			title,
			textContent,
			link,
			href,
			linkName,
			style,
			...others
		} = this.props;
		const { top, left, placement, hidden, isMounted } = this.state;
		const classNames = cx(`${flyoutClassName}__container`, {
			[`${placement}`]: placement,
			[`padding-${padding}`]: padding,
			hidden,
		});

		const arrowProps = {
			className: cx('ba-common-flyout-arrow', {
				[`${placement}`]: placement,
			}),
			ref: node => {
				this._arrow = node;
			},
		};

		const positions = {
			position: this._isFixedPosititonDefined() || !isMounted ? 'fixed' : 'absolute',
			top,
			left,
		};

		const flyoutProps = {
			...sanitize(others, Flyout),
			className: `${classNames} ${className}`,
			style: {
				...positions,
				...style,
			},
			onKeyDown: this._onKeyDown,
			tabIndex: '0',
			ref: node => {
				this._flyout = node;
			},
		};

		const flyout = (
			<div {...flyoutProps}>
				{showX && this._showX()}
				{title && this._title(title)}
				{textContent && this._textContent(textContent)}
				{(href || link) && this._link(href, linkName, link)}
				{children}
				{!hideArrow && <div {...arrowProps} />}
			</div>
		);

		return domNodeToAttachTo && isMounted
			? ReactDOM.createPortal(flyout, domNodeToAttachTo)
			: flyout;
	}
}
